import {
  Modal,
  PrimaryButton,
  SearchField,
  SecondaryButton,
  SnackbarStatus,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOptionGroups } from 'src/hooks/useOptionGroups';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useItemsSelection } from 'src/hooks/useItemsSelection';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { assignOptionGroupToItem } from 'src/api/assignOptionGroupToItem';
import { QueryKeys } from 'src/hooks/QueryKeys';
import createMenuItemModalImage from 'src/assets/images/create-menu-item-modal.png';
import styles from './AssignOptionGroupModal.module.scss';
import SelectableListWithControls from 'src/components/SelectableListWithControls';
import { toMap, getRangeSubtitle } from 'src/helpers';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { OptionGroup } from 'src/api/getOptionGroups/types';

import { TMenuItemType } from '../../types/menu.types';
import { OptionGroupType } from '../../constants';

interface ContentProps {
  closeModal: () => void;
  itemId: string;
  itemName: string;
  itemType: TMenuItemType;
  itemRanges?: string[];
  existingOptionGroupIds: string[];
  handleCreateGroup?: () => void;
}

interface AssignOptionGroupModalProps extends ContentProps {
  open: boolean;
}

const toOptionGroupOption = (optionGroup: OptionGroup, itemId: string) => ({
  id: optionGroup.option_group_id,
  label: optionGroup.name,
  subtitle: getRangeSubtitle(optionGroup.ranges),
  rightText: `${optionGroup.options.length} ${pluralize('product', optionGroup.options.length)}`,
  disabled: optionGroup.options?.some((option) => option.item_id === itemId) ?? false,
});

const Content = ({
  itemId,
  itemName,
  closeModal,
  itemType,
  itemRanges,
  existingOptionGroupIds,
  handleCreateGroup,
}: ContentProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const debouncedSearchTerm = useDebounce(searchTerm);

  const isBundle = itemType === OptionGroupType.BUNDLE;
  const optionGroupType = isBundle ? [OptionGroupType.BUNDLE] : [OptionGroupType.MODIFIER];
  const rangesFilter = isBundle && itemRanges?.length > 0 ? itemRanges : undefined;

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useOptionGroups({
    name: debouncedSearchTerm,
    type: optionGroupType,
    ranges: rangesFilter,
  });

  const optionGroupOptions = useMemo(
    () => data?.pages?.flatMap((page) => page.data.map((og) => toOptionGroupOption(og, itemId))) ?? [],
    [data, itemId],
  );

  const optionGroupOptionsMap = useMemo(() => toMap(optionGroupOptions, 'id'), [optionGroupOptions]);

  const {
    selectedIds: selectedOptionGroups,
    selectedItems: selectedOptionGroupOptions,
    handleSelectionChange,
    reset: resetSelection,
  } = useItemsSelection(optionGroupOptionsMap);

  const resetFields = () => {
    resetSelection();
    setSearchTerm('');
    closeModal();
  };

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);

      await assignOptionGroupToItem(itemId, [...existingOptionGroupIds, ...selectedOptionGroups]);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.item, itemId] });

      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `Successfully assigned ${selectedOptionGroups.length} ${pluralize('option group', selectedOptionGroups.length)} to ${itemName}`,
      });

      resetFields();
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Something went wrong whilst assigning the option group',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const groupTypeLabel = isBundle ? 'bundle' : 'modifier';

  return (
    <div className={styles['modal-content']}>
      <Typography size="xxlarge">
        Add {groupTypeLabel} groups to <span className={styles['bold-text']}>{itemName}</span>
      </Typography>
      <SelectableListWithControls
        options={optionGroupOptions}
        selectedOptionsIds={selectedOptionGroups}
        onOptionChange={handleSelectionChange}
        collapsible={{ selectedOptionsData: selectedOptionGroupOptions }}
        listAction={
          handleCreateGroup ? { label: `Create ${groupTypeLabel} group`, onClick: handleCreateGroup } : undefined
        }
        listDetails={{ title: isBundle ? 'Bundle Groups:' : 'Modifier Groups:', isLoading }}
        pagination={{ hasNextPage, onLoadMore: fetchNextPage, isLoadingMore: isFetchingNextPage }}
      >
        <SearchField
          placeholder={`Search for a ${groupTypeLabel} group`}
          onChange={(e) => setSearchTerm(e.target.value)}
          isDisabled={isSubmitting}
        />
      </SelectableListWithControls>

      <div className={styles['button-wrapper']}>
        <ButtonContainer
          left={
            <SecondaryButton size="full-width" buttonStyle="outline" onClick={resetFields}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              onClick={handleConfirm}
              disabled={selectedOptionGroups.length === 0 || isSubmitting}
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
    </div>
  );
};

export const AssignOptionGroupModal = ({
  open,
  closeModal,
  itemId,
  itemName,
  itemType,
  itemRanges,
  existingOptionGroupIds,
  handleCreateGroup,
}: AssignOptionGroupModalProps) => (
  <Modal open={open} closeModal={closeModal} image={createMenuItemModalImage} imageAlt="menu item">
    <Content
      itemId={itemId}
      itemName={itemName}
      closeModal={closeModal}
      itemType={itemType}
      itemRanges={itemRanges}
      existingOptionGroupIds={existingOptionGroupIds}
      handleCreateGroup={handleCreateGroup}
    />
  </Modal>
);
